import { Helmet } from 'react-helmet-async';

const SITE = 'CarLens Academy';
const DEFAULT_IMG =
  'https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200';

/**
 * Central SEO component: title, meta description, canonical, Open Graph,
 * Twitter cards, and JSON-LD structured data (Article + Breadcrumb + FAQ).
 */
export const Seo = ({
  title,
  description,
  image,
  path = '',
  type = 'website',
  article,
  breadcrumbs = [],
  faq = [],
  extraSchemas = [],
  siteName = SITE,
  noindex = false,
}) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://carlensauto.com';
  const url = `${origin}${path}`;
  const img = image || DEFAULT_IMG;
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  const graphs = [];

  if (article) {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title || title,
      description,
      image: [img],
      author: { '@type': 'Organization', name: article.author || 'CarLens' },
      publisher: {
        '@type': 'Organization',
        name: 'CarLens Inspections',
        logo: {
          '@type': 'ImageObject',
          url: 'https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png',
        },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      dateModified: article.dateModified || '2026-06-01',
    });
  }

  if (breadcrumbs.length) {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: `${origin}${b.path}`,
      })),
    });
  }

  if (faq.length) {
    graphs.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  extraSchemas.forEach((s) => graphs.push(s));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />

      {graphs.map((g, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(g)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
